import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Container, Draggable, DropResult } from 'react-smooth-dnd';

import { Surface } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { cn } from '@/utils/tailwind';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants from '@/constants';

import { TaskProps } from '@/types/types';

import PlanStepContainer, { SectionFunctionProps } from './PlanStepContainer';

import {
  Plan,
  PlanContent,
  PlanSection,
  PlanStep,
} from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';
import Section from '@/routes/TrainingPlans/BuildPlans/EditablePlanItems/Section';

interface PlanItemsProps {
  plan: Plan;
  addProcedure: (id?: string) => void;
  addQuiz: (quiz: string) => void;
  setIsDrop: (isDrop: boolean) => void;
  editProcedureResource?: ({
    stepId,
    sectionId,
  }: {
    stepId: string;
    sectionId?: string;
  }) => void;
  isReadOnly?: boolean;
}

interface PlanItemDropPayload {
  id: string;
  sectionId?: string;
}

const isPlanItemDropPayload = (
  value: unknown,
): value is PlanItemDropPayload => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as { id?: unknown; sectionId?: unknown };

  return (
    typeof candidate.id === 'string' &&
    (candidate.sectionId === undefined ||
      typeof candidate.sectionId === 'string')
  );
};

const PlanItems: React.FC<PlanItemsProps> = ({
  plan: planInput,
  addProcedure,
  addQuiz,
  setIsDrop,
  editProcedureResource,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const { updateContent, deleteStep, deleteSection, renameSection } =
    useUpdatePlan();
  const [editMode, setEditMode] = useState(false);
  const [sectionBeingRenamed, setSectionBeingRenamed] = useState({
    id: '',
    name: '',
  });
  const [taskToDelete, setTaskToDelete] = useState<{
    id: string;
    name: string;
    sectionId: string | null;
    type: string;
  }>({
    id: '',
    name: '',
    sectionId: '',
    type: '',
  });
  const [showDeleteTaskPopup, setShowDeleteTaskPopup] = useState(false);
  const [showDeleteSectionPopup, setShowDeleteSectionPopup] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState('');
  const [indexToAdd, setIndexToAdd] = useState<number>();
  const [localPlan, setLocalPlan] = useState(planInput);
  const [taskUpdated, setTaskUpdated] = useState(false);

  const { data: auth } = useAuth();
  const locationsWithAtLeastLeader =
    auth?.allLocationsWithAtLeastLeaderPermissions ?? [];

  useEffect(() => {
    setLocalPlan(planInput);
  }, [planInput]);

  const onDeleteSectionCancel = () => {
    setSectionBeingRenamed({ id: '', name: '' });
    setShowDeleteSectionPopup(false);
  };

  const onDeleteSection = async (section: { id: string; name: string }) => {
    setSectionBeingRenamed({ id: '', name: '' });
    setShowDeleteSectionPopup(false);
    await deleteSection(planInput, section.id);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${section.name} ${t('TrainingPlans.toastMessage.sectionDeleted')}`}
      </ToastMessageBlock>
    ));
  };

  const deleteSectionPopUp = (sectionId: string, sectionName: string) => {
    setSectionBeingRenamed({ id: sectionId, name: sectionName });
    setShowDeleteSectionPopup(true);
  };

  const isAllowedToEdit = () => locationsWithAtLeastLeader.length > 0;

  const renameSectionPopUp = (sectionId: string) => {
    setEditMode(true);
    setSectionBeingRenamed({ id: sectionId, name: '' });
  };

  const updateSectionName = (updatedSectionTitle: string) => {
    if (!updatedSectionTitle.trim()) return;
    const existingName = localPlan.content?.sections?.find(
      s => s.id === sectionBeingRenamed.id,
    )?.name;
    renameSection(planInput, sectionBeingRenamed.id, {
      ...existingName,
      [userLanguage]: updatedSectionTitle,
    });
  };

  const updateTask = (task: TaskProps, sectionId: string) => {
    const sections = localPlan.content?.sections?.map(section =>
      section.id === sectionId
        ? {
            ...section,
            steps: section.steps?.map(step =>
              step.id === task.id
                ? {
                    ...step,
                    name: { ...step.name, [userLanguage]: task.name },
                    note: { ...step.note, [userLanguage]: task.note },
                    urls: [
                      {
                        urlSet: {
                          ...step.urls?.[0]?.urlSet,
                          [userLanguage]: task.url,
                        },
                      },
                    ],
                  }
                : step,
            ),
          }
        : section,
    );

    const steps = localPlan.content?.steps?.map(step =>
      step.id === task.id
        ? {
            ...step,
            name: { ...step.name, [userLanguage]: task.name },
            note: { ...step.note, [userLanguage]: task.note },
            urls: [
              {
                urlSet: {
                  ...step.urls?.[0]?.urlSet,
                  [userLanguage]: task.url,
                },
              },
            ],
          }
        : step,
    );

    const content = sectionId
      ? { sections, steps: localPlan.content?.steps }
      : { steps, sections: localPlan.content?.sections };

    updateContent(planInput, content);
    setTaskUpdated(true);
  };

  const onUpdate = (
    type: string,
    updatedSectionTitle: string | null,
    task: TaskProps,
    sectionId: string,
  ) => {
    if (
      type === Constants.TRAINING_MENU_OPTIONS.SECTION &&
      updatedSectionTitle
    ) {
      updateSectionName(updatedSectionTitle);
    } else {
      updateTask(task, sectionId);
    }
    setEditMode(false);
    setSectionBeingRenamed({ id: '', name: '' });
  };

  const editSectionTitle = (section: PlanSection) =>
    isAllowedToEdit() && editMode && sectionBeingRenamed.id === section.id;

  const onDeleteTaskInsideSection = ({
    id,
    name,
    sectionId,
    type,
  }: SectionFunctionProps) => {
    setTaskToDelete({ id, name, sectionId, type });
    setShowDeleteTaskPopup(true);
  };

  const onDeleteTask = async () => {
    await deleteStep(planInput, taskToDelete.id, taskToDelete.sectionId);
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id}>
        {`${taskToDelete.name} ${t('TrainingPlans.toastMessage.deleted')}`}
      </ToastMessageBlock>
    ));
    setShowDeleteTaskPopup(false);
  };

  const onCancel = () => {
    setEditMode(false);
  };

  const onDeleteTaskOutsideSection = ({
    id,
    name,
    type,
  }: {
    id: string;
    name: string;
    type: string;
  }) => {
    setTaskToDelete({ id, name, sectionId: null, type });
    setShowDeleteTaskPopup(true);
  };

  const moveItem = <T,>(arr: T[], from: number, to: number): T[] => {
    const item = arr[from];
    if (!item) return arr;
    const result = arr.slice();
    result.splice(from, 1);
    result.splice(to, 0, item);
    return result;
  };

  const onDrop = (
    dropResult: DropResult,
    type?: string,
    _sectionId?: string,
  ) => {
    if (
      dropResult.removedIndex === null ||
      dropResult.removedIndex === dropResult.addedIndex
    ) {
      return;
    }

    const { removedIndex, addedIndex } = dropResult;
    if (addedIndex === null) return;

    const payload = isPlanItemDropPayload(dropResult.payload)
      ? dropResult.payload
      : undefined;

    setIsDrop(true);

    const sections = localPlan.content?.sections ?? [];
    const steps = localPlan.content?.steps ?? [];

    const applyUpdate = (update: Partial<PlanContent>) => {
      setLocalPlan(prevState => ({
        ...prevState,
        content: { ...prevState.content, ...update },
      }));
      updateContent(planInput, update);
    };

    // Section reorder
    if (type === Constants.DRAGGABLE_TYPES.SECTION) {
      applyUpdate({
        sections: moveItem(sections, removedIndex, addedIndex),
      });
      return;
    }

    // Top-level step reorder
    if (
      type === Constants.DRAGGABLE_TYPES.STEP &&
      !activeSectionId &&
      !payload?.sectionId
    ) {
      applyUpdate({
        steps: moveItem(steps, removedIndex, addedIndex),
      });
      return;
    }

    // Top-level step → into a section
    if (
      type === Constants.DRAGGABLE_TYPES.STEP &&
      activeSectionId &&
      !payload?.sectionId
    ) {
      const movedStep = steps.find(s => s.id === payload?.id);
      if (!movedStep || indexToAdd === undefined) return;

      applyUpdate({
        steps: steps.filter(s => s.id !== payload?.id),
        sections: sections.map(section => {
          if (section.id !== activeSectionId) return section;
          const withStep = [...(section.steps ?? []), movedStep];
          return {
            ...section,
            steps: moveItem(withStep, withStep.length - 1, indexToAdd),
          };
        }),
      });
      return;
    }

    // Step within same section reorder
    if (
      type === Constants.DRAGGABLE_TYPES.STEP_IN_SECTION &&
      payload?.sectionId &&
      payload.sectionId === activeSectionId
    ) {
      applyUpdate({
        sections: sections.map(section =>
          section.id === payload.sectionId
            ? {
                ...section,
                steps: moveItem(section.steps ?? [], removedIndex, addedIndex),
              }
            : section,
        ),
      });
      return;
    }

    // Step from section → top level or different section
    if (
      type === Constants.DRAGGABLE_TYPES.STEP_IN_SECTION &&
      payload?.sectionId &&
      payload.sectionId !== activeSectionId
    ) {
      const movedStep = sections
        .find(s => s.id === payload.sectionId)
        ?.steps?.find(s => s.id === payload.id);
      if (!movedStep || indexToAdd === undefined) return;

      const withoutStep = sections.map(section =>
        section.id === payload.sectionId
          ? {
              ...section,
              steps: section.steps?.filter(s => s.id !== payload.id),
            }
          : section,
      );

      if (!activeSectionId) {
        // → top level
        applyUpdate({
          sections: withoutStep,
          steps: moveItem([...steps, movedStep], steps.length, indexToAdd),
        });
      } else {
        // → different section
        applyUpdate({
          sections: withoutStep.map(section => {
            if (section.id !== activeSectionId) return section;
            const withStep = [...(section.steps ?? []), movedStep];
            return {
              ...section,
              steps: moveItem(withStep, withStep.length - 1, indexToAdd),
            };
          }),
        });
      }
    }
  };

  return (
    <>
      {localPlan.content?.steps && localPlan.content.steps.length > 0 && (
        <div className="w-full">
          {/** @ts-expect-error overload error */}
          <Container
            dragHandleSelector=".step-drag-handle"
            dropPlaceholder={{
              className: 'bg-grayscale-gray2 p-2.5 rounded-[5px]',
            }}
            getChildPayload={index => localPlan.content?.steps?.[index]}
            groupName="steps"
            nonDragAreaSelector=".pathway-disabled"
            onDragEnter={() => setActiveSectionId('')}
            onDrop={e => onDrop(e, Constants.DRAGGABLE_TYPES.STEP)}
            onDropReady={addedIndex =>
              setIndexToAdd(addedIndex.addedIndex ?? undefined)
            }
          >
            {localPlan.content.steps.map((step: PlanStep, idx: number) => (
              // @ts-expect-error it's yelling about children but we need children for the drag and drop
              <Draggable
                data-testid="Draggable"
                key={`${step.id}-${idx}-plan-step`}
                className={cn('my-6 first:mt-0 last:mb-0', {
                  'pathway-disabled': isReadOnly,
                })}
              >
                <Surface className="pathway-surface">
                  <PlanStepContainer
                    className="step-drag-handle"
                    isAllowedToEdit={isAllowedToEdit()}
                    language={userLanguage}
                    onCancel={onCancel}
                    onDelete={onDeleteTaskOutsideSection}
                    onEdit={(type, updatedSectionTitle, task, sectionId) =>
                      onUpdate(type, updatedSectionTitle, task, sectionId)
                    }
                    step={step}
                    taskUpdated={taskUpdated}
                    editProcedureResource={editProcedureResource}
                    isReadOnly={isReadOnly}
                  />
                </Surface>
              </Draggable>
            ))}
          </Container>
        </div>
      )}

      <div className="w-full">
        {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
        <Container
          dragHandleSelector=".section-drag-handle"
          dropPlaceholder={{
            className: 'bg-grayscale-gray2 p-2.5 rounded-[5px]',
          }}
          nonDragAreaSelector=".pathway-disabled"
          onDrop={e => onDrop(e, Constants.DRAGGABLE_TYPES.SECTION)}
        >
          {localPlan.content?.sections &&
            localPlan.content.sections.length > 0 &&
            localPlan.content.sections.map(
              (section: PlanSection, idx: number) => (
                // @ts-expect-error it's yelling about children but we need children for the drag and drop
                <Draggable
                  key={`${section.id}-${idx}-plan-sections`}
                  className={cn('my-6 first:mt-0 last:mb-0', {
                    'pathway-disabled': isReadOnly,
                  })}
                >
                  <Section
                    addProcedure={() => section.id && addProcedure(section.id)}
                    editProcedureResource={editProcedureResource}
                    addQuiz={() => section.id && addQuiz(section.id)}
                    deleteSectionPopUp={deleteSectionPopUp}
                    editSectionTitle={editSectionTitle}
                    index={idx}
                    isAllowedToEdit={isAllowedToEdit()}
                    language={userLanguage}
                    onCancel={onCancel}
                    onClickThreeDotMenuDeleteTask={onDeleteTaskInsideSection}
                    onDrop={(
                      e: {
                        addedIndex: number;
                        payload: { id: string; sectionId: string };
                        removedIndex: number;
                      },
                      id: string,
                    ) =>
                      onDrop(e, Constants.DRAGGABLE_TYPES.STEP_IN_SECTION, id)
                    }
                    onEdit={(
                      type: string,
                      updatedSectionTitle: string | null,
                      task: TaskProps,
                      id: string,
                    ) => onUpdate(type, updatedSectionTitle, task, id)}
                    plan={planInput}
                    renameSectionPopUp={renameSectionPopUp}
                    section={section}
                    setActiveSectionId={id => setActiveSectionId(id)}
                    setIndexToAdd={index =>
                      setIndexToAdd(index.addedIndex ?? undefined)
                    }
                    taskUpdated={taskUpdated}
                    isReadOnly={isReadOnly}
                  />
                </Draggable>
              ),
            )}
        </Container>
      </div>
      <ConfirmationModal
        bodyText={t('Generic.deleteObject', {
          name: sectionBeingRenamed.name,
          object: t('Generic.section').toLowerCase(),
        })}
        headerText={t('Generic.deleteHeader', {
          type: t('Generic.section'),
        })}
        isOpen={showDeleteSectionPopup}
        onClose={onDeleteSectionCancel}
        primaryButtonHandler={() => onDeleteSection(sectionBeingRenamed)}
        primaryButtonText={t('Button.delete')}
        primaryButtonVariant="destructive"
        secondaryButtonHandler={onDeleteSectionCancel}
        secondaryButtonText={t('Button.cancel')}
      />
      <ConfirmationModal
        bodyText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Generic.deleteObject', {
                name: taskToDelete.name,
                object: t('Generic.task').toLowerCase(),
              })
            : t('Generic.removeObject', {
                name: taskToDelete.name,
                object:
                  taskToDelete.type === Constants.STEP_TYPES.DOCUMENT
                    ? t('Generic.resource').toLowerCase()
                    : t('Generic.quiz').toLowerCase(),
              })
        }
        headerText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Generic.deleteHeader', {
                type: t('Generic.task'),
              })
            : t('Generic.removeHeader', {
                type:
                  taskToDelete.type === Constants.STEP_TYPES.DOCUMENT
                    ? t('Generic.resource')
                    : taskToDelete.type === Constants.STEP_TYPES.QUIZ
                      ? t('Generic.quiz')
                      : t('Generic.task'),
              })
        }
        isOpen={showDeleteTaskPopup}
        onClose={() => {
          setShowDeleteTaskPopup(false);
        }}
        primaryButtonHandler={onDeleteTask}
        primaryButtonText={
          taskToDelete.type === Constants.STEP_TYPES.TASK
            ? t('Button.delete')
            : t('Button.remove')
        }
        primaryButtonVariant="destructive"
        secondaryButtonHandler={() => {
          setShowDeleteTaskPopup(false);
        }}
        secondaryButtonText={t('Button.cancel')}
      />
    </>
  );
};

export default PlanItems;
